import { CoreApplicationContext } from '@eventbrite/context-gen';
import { getNarrowMenuInfo } from '@eventbrite/eds-global-header';
import { IconButton } from '@eventbrite/eds-icon-button';
import { MagnifyingGlassChunky as MagnifyingGlassChunkySvg } from '@eventbrite/eds-iconography';
import { HAS_DOCUMENT } from '@eventbrite/eds-utils';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import {
    FALLBACK_SUGGESTIONS,
    getCreateEventCTA,
    getFindEventCTA,
    getHelpMenu,
    getLogoInfo,
    getNarrowMenuLinks,
    getNonUserQuickLinks,
    getOrganizeMenu,
    getUserMenuInfo,
    logCreateEventCTAClick,
    logFindEventCTAClick,
} from '@eventbrite/site-header';
import loadable from '@loadable/component';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useGetStatsigExperiment } from '../../hooks/experiments';
import { FeatureFlags, RequestShape, UserShape } from '../../types/index';
import './ConsumerHeaderIndex.scss';
import { getBugQuickLink } from './core-js-global-copies';
import GlobalHeaderLogo from './GlobalHeaderLogo';
import {
    GlobalHeaderCallToAction,
    GlobalHeaderMenu,
    GlobalHeaderMenus,
    GlobalHeaderQuickLinks,
} from './GlobalHeaderNavigationBar';
import GlobalHeaderSearch, { getSearchInfo } from './GlobalHeaderSearch';

const LazySearchTakeover = loadable.lib(
    () => import('@eventbrite/search-takeover'),
);

interface ConsumerHeaderProps {
    env: CoreApplicationContext['env'] & {
        launchNewSignInSignUpLinks?: string;
        isManageOverviewActive?: boolean;
    };
    request: RequestShape;
    user: UserShape;
    onSearchSelect: Function;
    onAdditionalMenuTextItemSelect?: Function;
    handleSearch: Function;
    onSearchFocus?: Function;
    isHomePage?: boolean;
    isSearchPage?: boolean;
    isBrowsePage?: boolean;
    isMobile?: boolean;
    id?: string;
    featureFlags: FeatureFlags;
}

interface ConsumerHeaderState {
    searchQuery?: any;
    searchSuggestions?: String;
    isSearchTakeoverVisible?: null | boolean;
}

const ConsumerHeader: React.FunctionComponent<ConsumerHeaderProps> = (
    props,
) => {
    let organizeEvent: boolean;

    const [state, setState] = useState<ConsumerHeaderState>({
        searchQuery: props.env.searchQuery || '',
        searchSuggestions: FALLBACK_SUGGESTIONS,
        isSearchTakeoverVisible: null,
    });

    const CTAVariantValue = `${useGetStatsigExperiment(
        'organizer_and_attendee_cta_experiment',
        'variantNum',
        0,
    )}`;

    const { path: requestPath } = props.request;
    const isHomePage = requestPath === '/';

    /**
     * Lets you test the experiment without Statsig
     * To enable the experiment add the "organizeEvent"
     * query param to the end of the URL
     */
    if (HAS_DOCUMENT) {
        const params = new URLSearchParams(document?.location?.search || '');
        organizeEvent = params.has('organizeEvent');
    }

    const _handleSearchClick = () => {
        setState({ isSearchTakeoverVisible: true });
    };

    const _handleOnAdditionalMenuTextItemSelect = (
        value: string,
        index: number,
    ) => {
        if (props.onAdditionalMenuTextItemSelect) {
            props.onAdditionalMenuTextItemSelect(value, index);
        }
    };

    const _showCreateEventCTA = ({ canCreateEvents }: UserShape) => {
        // Only show the Create Event Cta if the user can create events, or is on the homepage
        return canCreateEvents || isHomePage;
    };

    const _getHeaderLinks = () => {
        const {
            env: {
                ebDomain,
                helpUrls,
                signinUrl,
                signupUrl,
                loginUrl,
                organizeUrls,
                serverUrl,
                launchNewSignInSignUpLinks,
            },
            user,
            featureFlags,
        } = props;

        const { isAuthenticated } = user;
        const isExperiment = organizeEvent || CTAVariantValue !== '0';

        const showCreateEventCTA = _showCreateEventCTA(user);

        const createEventCTA = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            isExperiment,
            { showCreateEventCTA },
            () => logCreateEventCTAClick(isHomePage),
        );

        const findEventCTA = getFindEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            { showCreateEventCTA },
            () => logFindEventCTAClick(isHomePage),
        );

        const additionalMenus = [
            getOrganizeMenu(organizeUrls, requestPath),
            getHelpMenu(helpUrls, requestPath),
        ];

        let linkComponents: any[] = [];

        // on the search page, show britelings a link to report a bug
        if (featureFlags.showReportBugLinkInHeader) {
            linkComponents = [
                <GlobalHeaderQuickLinks
                    key="bugLinks"
                    links={getBugQuickLink()}
                    shouldShowIcon={true}
                />,
            ];
        }

        if (!isAuthenticated) {
            const signinPath = launchNewSignInSignUpLinks
                ? signinUrl
                : loginUrl;
            const nonUserQuickLinks = getNonUserQuickLinks(
                requestPath,
                isAuthenticated,
                signinPath,
                signupUrl,
                { launchNewSignInSignUpLinks },
            );

            switch (CTAVariantValue) {
                case '0':
                    linkComponents = [
                        <GlobalHeaderMenus
                            key="additionalMenus"
                            additionalMenus={additionalMenus}
                            onAdditionalMenuTextItemSelect={
                                _handleOnAdditionalMenuTextItemSelect
                            }
                        />,
                        <GlobalHeaderCallToAction
                            key="createEventLinkNotAuth"
                            linkInfo={createEventCTA}
                            shouldShowIcon={false}
                        />,
                        <GlobalHeaderQuickLinks
                            key="nonUserQuickLinks"
                            links={nonUserQuickLinks}
                            shouldShowIcon={false}
                        />,
                    ];
                    break;
                case '1':
                    linkComponents = [
                        <GlobalHeaderCallToAction
                            key="createEventLinkNotAuth"
                            linkInfo={createEventCTA}
                            shouldShowIcon={false}
                        />,
                        <GlobalHeaderMenus
                            key="additionalMenus"
                            additionalMenus={additionalMenus}
                            onAdditionalMenuTextItemSelect={
                                _handleOnAdditionalMenuTextItemSelect
                            }
                        />,
                        <GlobalHeaderQuickLinks
                            key="nonUserQuickLinks"
                            links={nonUserQuickLinks}
                            shouldShowIcon={false}
                        />,
                    ];
                    break;
                case '2':
                    linkComponents = [
                        <GlobalHeaderCallToAction
                            key="findEventLinkNotAuth"
                            linkInfo={findEventCTA}
                            shouldShowIcon={false}
                        />,
                        <GlobalHeaderCallToAction
                            key="createEventLinkNotAuth"
                            linkInfo={createEventCTA}
                            shouldShowIcon={false}
                        />,
                        <GlobalHeaderMenus
                            key="additionalMenus"
                            additionalMenus={additionalMenus}
                            onAdditionalMenuTextItemSelect={
                                _handleOnAdditionalMenuTextItemSelect
                            }
                        />,
                        <GlobalHeaderQuickLinks
                            key="nonUserQuickLinks"
                            links={nonUserQuickLinks}
                            shouldShowIcon={false}
                        />,
                    ];
                    break;
                default:
                    linkComponents = [
                        <GlobalHeaderMenus
                            key="additionalMenus"
                            additionalMenus={additionalMenus}
                            onAdditionalMenuTextItemSelect={
                                _handleOnAdditionalMenuTextItemSelect
                            }
                        />,
                        <GlobalHeaderCallToAction
                            key="createEventLinkNotAuth"
                            linkInfo={createEventCTA}
                            shouldShowIcon={false}
                        />,
                        <GlobalHeaderQuickLinks
                            key="nonUserQuickLinks"
                            links={nonUserQuickLinks}
                            shouldShowIcon={false}
                        />,
                    ];
                    break;
            }
        }

        return <div className="consumer-header__links">{linkComponents}</div>;
    };

    const _getGlobalHeaderSearch = (searchInfo: {
        label: string | GenericLazyString;
    }) => {
        const { id, isSearchPage, isBrowsePage } = props;

        if (!isSearchPage) {
            return (
                <GlobalHeaderSearch
                    id={`${id || 'global-header'}-desktop-search`}
                    searchInfo={searchInfo}
                    isHomePage={isHomePage}
                    isBrowsePage={isBrowsePage || false}
                    setRenderSearchTakeover={(val: boolean) =>
                        setState({ isSearchTakeoverVisible: val })
                    }
                />
            );
        }

        return <div className="consumer-header__search__placeholder" />;
    };

    const _getMobileHeaderLinks = () => {
        const {
            user,
            env: {
                adminServerUrl,
                ebDomain,
                loginUrl,
                logoutUrl,
                signinUrl,
                signupUrl,
                helpUrls,
                organizeUrls,
                isManageOverviewActive,
                serverUrl,
                launchNewSignInSignUpLinks,
            },
            featureFlags,
        } = props;

        const { isAuthenticated } = user;
        const additionalMenus = [
            getOrganizeMenu(organizeUrls, requestPath),
            getHelpMenu(helpUrls, requestPath),
        ];
        const userMenuInfo = getUserMenuInfo({
            adminServerUrl,
            featureFlags,
            isManageOverviewActive,
            logoutUrl,
            requestPath,
            serverUrl,
            showBrowseEvents: true,
            user,
        });

        const signinPath = launchNewSignInSignUpLinks ? signinUrl : loginUrl;
        const nonUserQuickLinks = getNonUserQuickLinks(
            requestPath,
            isAuthenticated,
            signinPath,
            signupUrl,
            { launchNewSignInSignUpLinks },
        );

        const showCreateEventCTA = _showCreateEventCTA(user);
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            null,
            { showCreateEventCTA },
            () => logCreateEventCTAClick(isHomePage),
        );

        const narrowMenuInfo = !isAuthenticated
            ? getNarrowMenuLinks({
                  serverUrl,
                  requestPath,
                  createEventCTA: callToActionInfo,
              })
            : getNarrowMenuInfo(
                  userMenuInfo,
                  additionalMenus,
                  [],
                  callToActionInfo,
                  [],
                  true,
              );

        let narrowMenu;

        if (narrowMenuInfo) {
            const className = classNames(
                'eds-global-header__narrow-menu',
                'eds-show-down-mn',
            );

            narrowMenu = [narrowMenuInfo].map((menuInfo) => (
                <span key={menuInfo.label} className={className}>
                    <GlobalHeaderMenu
                        menuInfo={menuInfo}
                        data-spec="global-header-narrow-menu"
                        noLabel={true}
                    />
                </span>
            ));
        }

        return (
            <div className="consumer-header__links">
                {narrowMenu}
                <GlobalHeaderQuickLinks
                    links={nonUserQuickLinks}
                    shouldShowIcon={false}
                />
            </div>
        );
    };

    const { id, env, user, isSearchPage, featureFlags } = props;
    const { searchQuery, searchSuggestions, isSearchTakeoverVisible }: any =
        state;
    const { serverUrl, adminServerUrl, logoutUrl } = env;
    const logoInfo = getLogoInfo(serverUrl, requestPath, {
        iconWidth: '110px',
    });
    const searchInfo = getSearchInfo(serverUrl, searchQuery, searchSuggestions);
    const userMenuInfo = getUserMenuInfo({
        adminServerUrl,
        featureFlags,
        logoutUrl,
        requestPath,
        serverUrl,
        showBrowseEvents: true,
        user,
    });

    return (
        <header
            id={id || 'global-header'}
            className={`consumer-header ${
                isHomePage ? '' : 'consumer-header__not-homepage'
            }`}
            data-spec="consumer-header"
        >
            <a
                className="consumer-header__skip-links eds-is-hidden-accessible"
                href="#skip-heading"
            >
                {gettext('Skip Main Navigation')}
            </a>
            <div className="consumer-header__main" data-role="header">
                <div
                    className="consumer-header__content consumer-header__desktop eds-show-up-md"
                    data-spec="consumer-header-desktop"
                >
                    <GlobalHeaderLogo info={logoInfo} />
                    {_getGlobalHeaderSearch(searchInfo)}
                    {_getHeaderLinks()}
                    <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                </div>

                <div
                    className="consumer-header__content consumer-header__mobile eds-show-down-mn eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1"
                    data-spec="consumer-header-mobile"
                >
                    <div className="consumer-header__mobile-links eds-align--space-between">
                        <GlobalHeaderLogo info={logoInfo} />
                        <div className="eds-align--center-vertical">
                            {!isSearchPage && (
                                <div className="consumer-header__mobile-search-button">
                                    <IconButton
                                        iconType={<MagnifyingGlassChunkySvg />}
                                        title={gettext('Search button')}
                                        iconColor="grey-600"
                                        onClick={_handleSearchClick}
                                    />
                                </div>
                            )}
                            {_getMobileHeaderLinks()}
                        </div>
                    </div>
                </div>
            </div>

            <span id="skip-heading" className="eds-is-hidden-accessible">
                {gettext('Page Content')}
            </span>
            {isSearchTakeoverVisible !== null ? (
                <LazySearchTakeover>
                    {({ SearchTakeover }: { SearchTakeover: any }) => (
                        <SearchTakeover
                            domNodeId="root"
                            onClose={() =>
                                setState({
                                    isSearchTakeoverVisible: false,
                                })
                            }
                            id="search-takeover"
                            isVisible={isSearchTakeoverVisible}
                        />
                    )}
                </LazySearchTakeover>
            ) : null}
        </header>
    );
};

export default ConsumerHeader;
